import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MODULES } from '@/config/modules';
import { useConsoleStore } from '@/stores/consoleStore';
import { usePendingProposals } from '@/stores/proposalSelectors';
import { useProject, useEntity } from '@/stores/selectors';
import { useFocus } from '@/lib/useFocus';
import { isTauriRuntime } from '@/engine/tauriBridge';
import { entitySummary } from '@/config/entityMeta';
import { ConsoleTimeline } from './ConsoleTimeline';
import { buildViewPreamble } from './preamble';

/**
 * 글로벌 콘솔 도크(K2, ⌘J) — 어느 화면에서든 커널을 부른다. 레일 탭·/console과 **같은 세션**.
 * 컨텍스트 = 현재 화면 + 선택 엔티티(인스펙터 포커스)가 자동 주입. Esc로 닫힘.
 */
export function ConsoleDock() {
  const { dockOpen, toggleDock, closeDock, events, running, hydrate, send, cancel } = useConsoleStore();
  const project = useProject();
  const pending = usePendingProposals();
  const location = useLocation();
  const [focusId] = useFocus();
  const focused = useEntity(focusId || undefined);
  const [prompt, setPrompt] = useState('');
  const ready = isTauriRuntime();

  const onHome = location.pathname === '/';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        // F0: 홈에선 커널이 본체 — 도크 대신 컴포저 포커스. 수동 화면에서만 도크.
        if (window.location.pathname === '/') {
          window.dispatchEvent(new CustomEvent('loom:focus-composer'));
        } else {
          toggleDock();
        }
      } else if (e.key === 'Escape' && useConsoleStore.getState().dockOpen) {
        closeDock();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleDock, closeDock]);

  useEffect(() => {
    if (dockOpen && project?.id) void hydrate(project.id);
  }, [dockOpen, project?.id, hydrate]);

  if (!dockOpen || onHome) return null;

  const viewLabel = MODULES.find((m) => (m.path === '/' ? location.pathname === '/' : location.pathname.startsWith(m.path)))?.label ?? '알 수 없는 화면';

  const submit = async () => {
    if (!prompt.trim()) return;
    const { preamble, note } = buildViewPreamble({
      viewLabel,
      entity: focused ? { name: focused.name, type: focused.type, summary: entitySummary(focused) || undefined } : null,
    });
    setPrompt('');
    await send(prompt, { preamble, contextNote: note });
  };

  return (
    <aside className="fixed inset-y-0 right-0 z-50 flex w-[420px] max-w-[90vw] flex-col border-l border-line bg-bg/95 shadow-pop backdrop-blur-xl">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <div className="field-key flex-1">Loom 콘솔 — 커널</div>
        <span className="mono text-[10px] text-fainter">⌖ {viewLabel}{focused ? ` · ${focused.name}` : ''}</span>
        <button type="button" onClick={closeDock} aria-label="콘솔 닫기" className="rounded px-1.5 text-[12px] text-fainter transition hover:text-dim">
          ✕
        </button>
      </div>

      {pending.length > 0 && (
        <Link
          to="/"
          onClick={closeDock}
          className="flex items-center gap-2 border-b border-amber-400/20 bg-amber-500/5 px-3 py-1.5 text-[11px] text-amber-200 transition hover:bg-amber-500/10"
        >
          승인 큐 — 대기 제안 <b>{pending.length}</b>건 <span className="ml-auto">검토하러 가기 →</span>
        </Link>
      )}

      <ConsoleTimeline events={events.slice(-50)} className="flex-1 overflow-y-auto p-2" />

      <div className="border-t border-line p-2">
        <div className="flex items-end gap-1.5">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
            }}
            rows={2}
            autoFocus
            placeholder={ready ? `${viewLabel} 기준으로 지시… (⌘Enter)` : '데스크톱 앱에서 에이전트 구동(이력은 표시)'}
            disabled={!ready}
            className="min-w-0 flex-1 resize-none rounded border border-line bg-bg px-2 py-1.5 text-xs text-ink outline-none focus:border-star disabled:cursor-not-allowed"
          />
          {running ? (
            <button type="button" onClick={() => void cancel()} className="shrink-0 rounded border border-red-400/50 bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-300 transition hover:bg-red-500/15">
              취소
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!ready || !prompt.trim()}
              className="shrink-0 rounded border border-star/50 bg-star/10 px-2.5 py-1.5 text-[11px] text-star transition hover:bg-star/15 disabled:cursor-not-allowed disabled:border-line disabled:bg-bg disabled:text-fainter"
            >
              보내기
            </button>
          )}
        </div>
        <p className="mt-1 text-right font-mono text-[9px] text-fainter">⌘J 토글 · Esc 닫기 · 전체는 커널 홈(상단 커널 버튼)</p>
      </div>
    </aside>
  );
}
